import React, { useCallback, useMemo, useState } from 'react';
import cn from 'clsx';
import { Tab, TabList } from 'fronton-react';
import { Link, PictureWithLinks, useModelJsonSelector } from 'modules/model-json';
import { enhanceImageUrl, replaceSpecialChars, removeSpecialCharacters, createElementKey } from 'utils';
import {
  CardLinkWithGAClickButton,
  CardMedia,
  MediaCard,
  MediaCardContent,
  MediaCardTitle,
  Typography,
} from 'common/components';
import { strictBreakpoints } from 'common/styles/breakpoints';

import { analyticsUtils } from 'utils/analytics-utils';
import {
  bigCardMediaWrapper,
  bigCategoryCard,
  bigCategoryContent,
  bigCategoryListStyle,
  bigCategoryMedia,
  bigCategoryText,
  cardMediaWrapper,
  categoryCardContent,
  categoriesSection,
  categoriesWrapper,
  categoryMedia,
  categoryImage,
  categoryLink,
  categoryListStyle,
  categoryItemStyle,
  inactiveCategoryTab,
  categoryTabList,
  categoryTitle,
} from './categories-categories-styles';

/**
 * Вью секции с категориями по сезонам
 */
export const CategoriesSection = () => {
  const { tabWithPictures } = useModelJsonSelector();

  const activeTabIndex = useMemo(() => (tabWithPictures?.[0].isActive ? 0 : 1), [tabWithPictures]);
  const tabList = useMemo(() => tabWithPictures?.map(tab => tab.title), [tabWithPictures]);

  const [activeTab, setActiveTab] = useState<number>(activeTabIndex);

  /**
   * Обработчик нажатия на таб
   */
  const handleChangeTab = useCallback(
    (e: React.SyntheticEvent, tab: number) => {
      setActiveTab(tab);
      analyticsUtils.clickButtonEvent('landingSad', tabList ? tabList[tab] : '', 'Сад и огород');
    },
    [setActiveTab, tabList]
  );

  if (!tabWithPictures || !tabList || tabWithPictures.length === 0) {
    return null;
  }

  return (
    <section className={categoriesSection}>
      <TabList active={activeTab} className={categoryTabList} size="l" onChangeTab={handleChangeTab}>
        {tabList.map((tab: string, index: number) => (
          <Tab size="l" key={createElementKey('categories-tab', index)}>
            <Typography variant="h1" className={cn({ [inactiveCategoryTab]: index !== activeTab })}>
              {tab}
            </Typography>
          </Tab>
        ))}
      </TabList>

      <div className={categoriesWrapper}>
        {tabWithPictures[activeTab].pictureWithLinks.map(
          (
            {
              title,
              bannerMobileRef,
              bannerTabletRef,
              bannerDesktopRef,
              bannerLargeRef,
              links,
            }: PictureWithLinks,
            index: number
          ) => (
            <MediaCard
              className={cn(categoryMedia, { [bigCategoryCard]: index === 0 })}
              key={createElementKey('garden-category', index)}>
              <div className={cn(cardMediaWrapper, { [bigCardMediaWrapper]: index === 0 })}>
                <picture>
                  <source
                    srcSet={enhanceImageUrl(bannerLargeRef)}
                    media={strictBreakpoints['--only-largeDesktop']}
                  />
                  <source
                    srcSet={enhanceImageUrl(bannerDesktopRef)}
                    media={strictBreakpoints['--only-desktop']}
                  />
                  <source
                    srcSet={enhanceImageUrl(bannerTabletRef)}
                    media={strictBreakpoints['--only-tablet']}
                  />
                  <CardMedia
                    alt={title}
                    className={cn(categoryImage, { [bigCategoryMedia]: index === 0 })}
                    height="146"
                    src={enhanceImageUrl(bannerMobileRef)}
                    title={title}
                    width="288"
                  />
                </picture>
              </div>
              <MediaCardContent className={cn(categoryCardContent, { [bigCategoryContent]: index === 0 })}>
                <MediaCardTitle
                  className={cn(categoryTitle, { [bigCategoryText]: index === 0 })}
                  variant="h2">
                  {replaceSpecialChars(title)}
                </MediaCardTitle>
                <ul className={cn(categoryListStyle, { [bigCategoryListStyle]: index === 0 })}>
                  {links.map(({ link, label }: Link, categoryIndex: number) => (
                    <li className={categoryItemStyle} key={createElementKey('category', categoryIndex)}>
                      <CardLinkWithGAClickButton
                        className={cn(categoryLink, { [bigCategoryText]: index === 0 })}
                        elementBlock="landingSad"
                        elementName={removeSpecialCharacters(label)}
                        elementText={`${tabList[activeTab]} - ${removeSpecialCharacters(title)}`}
                        href={link}
                        title={label}>
                        {replaceSpecialChars(label)}
                      </CardLinkWithGAClickButton>
                    </li>
                  ))}
                </ul>
              </MediaCardContent>
            </MediaCard>
          )
        )}
      </div>
    </section>
  );
};
